<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onMounted, ref } from 'vue';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import { useI18n } from 'vue-i18n';
import {
  useStoreGetters,
  useStore,
  useMapGetter,
} from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import axios from 'axios';

import AddAgent from './AddAgent.vue';
import EditAgent from './EditAgent.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import ConfirmationModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const loading = ref({});
const showAddPopup = ref(false);
const showDeletePopup = ref(false);
const showEditPopup = ref(false);
const agentAPI = ref({ message: '' });
const currentAgent = ref({});
const forceSignMessage = ref(false);
const hideAllChatsForAgent = ref(false);
const autoAssignEnabled = ref(false);
const confirmationModal = ref(null);

const deleteConfirmText = computed(
  () => `${t('AGENT_MGMT.DELETE.CONFIRM.YES')} ${currentAgent.value.name}`
);
const deleteRejectText = computed(() => {
  return `${t('AGENT_MGMT.DELETE.CONFIRM.NO')} ${currentAgent.value.name}`;
});
const deleteMessage = computed(() => {
  return ` ${currentAgent.value.name}?`;
});

const agentList = computed(() => getters['agents/getAgents'].value);
const uiFlags = computed(() => getters['agents/getUIFlags'].value);
const currentUserId = computed(() => getters.getCurrentUserID.value);
const customRoles = useMapGetter('customRole/getCustomRoles');

onMounted(() => {
  store.dispatch('agents/get');
  // Só busca custom roles se for admin (gerente não precisa)
  if (isAdmin.value) {
    store.dispatch('customRole/getCustomRole');
  }
  // Só busca configuração de assinatura se for admin
  if (isAdmin.value) {
    fetchSignMessageSetting();
  }
  // Só busca configurações de visibilidade e auto-atribuição se for admin
  if (isAdmin.value) {
    fetchAgentVisibilitySetting();
    fetchAutoAssignSetting();
  }
});

const findCustomRole = agent =>
  customRoles.value.find(role => role.id === agent.custom_role_id);

const getAgentRoleName = agent => {
  if (!agent.custom_role_id) {
    return t(`AGENT_MGMT.AGENT_TYPES.${agent.role.toUpperCase()}`);
  }
  const customRole = findCustomRole(agent);
  return customRole ? customRole.name : '';
};

const getAgentRolePermissions = agent => {
  if (!agent.custom_role_id) {
    return [];
  }
  const customRole = findCustomRole(agent);
  return customRole?.permissions || [];
};

const verifiedAdministrators = computed(() => {
  return agentList.value.filter(
    agent => agent.role === 'administrator' && agent.confirmed
  );
});

const showEditAction = agent => {
  return currentUserId.value !== agent.id;
};

const showDeleteAction = agent => {
  if (currentUserId.value === agent.id) {
    return false;
  }

  if (!agent.confirmed) {
    return true;
  }

  if (agent.role === 'administrator') {
    return verifiedAdministrators.value.length !== 1;
  }
  return true;
};

const showAlertMessage = message => {
  loading.value[currentAgent.value.id] = false;
  currentAgent.value = {};
  agentAPI.value.message = message;
  useAlert(message);
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = agent => {
  showEditPopup.value = true;
  currentAgent.value = agent;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = agent => {
  showDeletePopup.value = true;
  currentAgent.value = agent;
};
const closeDeletePopup = () => {
  showDeletePopup.value = false;
};

const deleteAgent = async id => {
  try {
    await store.dispatch('agents/delete', id);
    showAlertMessage(t('AGENT_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    showAlertMessage(t('AGENT_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const confirmDeletion = () => {
  loading.value[currentAgent.value.id] = true;
  closeDeletePopup();
  deleteAgent(currentAgent.value.id);
};

const getCookie = name => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
};

const getAuthHeaders = () => {
  const cookie = getCookie('cw_d_session_info');
  if (!cookie) return {};
  try {
    const sessionInfo = JSON.parse(cookie);
    return {
      'access-token': sessionInfo['access-token'],
      client: sessionInfo.client,
      uid: sessionInfo.uid,
    };
  } catch (e) {
    console.error('Erro ao ler o cookie:', e);
    return {};
  }
};

const fetchSignMessageSetting = async () => {
  try {
    const currentAccount = store.getters.getCurrentAccount;
    const response = await axios.get(
      `/api/v1/accounts/${currentAccount.id}/sign_messages`,
      {
        headers: getAuthHeaders(),
        withCredentials: true,
      }
    );

    if (response.data.success) {
      forceSignMessage.value = response.data.sign_message;
    }
  } catch (error) {
    console.error('Erro ao buscar configuração de assinatura:', error);
    // Manter o valor padrão (false) em caso de erro
    forceSignMessage.value = false;
  }
};

const toggleForceSignMessage = async () => {
  try {
    const currentAccount = store.getters.getCurrentAccount;
    const response = await axios.put(
      `/api/v1/accounts/${currentAccount.id}/sign_messages`,
      { sign_message: forceSignMessage.value },
      {
        headers: getAuthHeaders(),
        withCredentials: true,
      }
    );

    if (response.data.success) {
      useAlert(`Configuração de assinatura de mensagem ${forceSignMessage.value ? 'ativada' : 'desativada'} com sucesso!`);
    } else {
      throw new Error(response.data.error || 'Erro desconhecido');
    }
  } catch (error) {
    console.error('Erro ao atualizar configuração de assinatura:', error);
    useAlert('Erro ao atualizar configuração de assinatura de mensagem. Tente novamente.');
    // Reverter o estado do toggle em caso de erro
    forceSignMessage.value = !forceSignMessage.value;
  }
};

const fetchAgentVisibilitySetting = async () => {
  try {
    const currentAccount = store.getters.getCurrentAccount;
    const response = await axios.get(
      `/api/v1/accounts/${currentAccount.id}/agent_visibility_settings`,
      {
        headers: getAuthHeaders(),
        withCredentials: true,
      }
    );

    if (response.data.success) {
      hideAllChatsForAgent.value = response.data.hide_all_chats_for_agent;
    }
  } catch (error) {
    console.error('Erro ao buscar configuração de visibilidade:', error);
    hideAllChatsForAgent.value = false;
  }
};

const toggleHideAllChatsForAgent = async () => {
  try {
    const currentAccount = store.getters.getCurrentAccount;
    const response = await axios.put(
      `/api/v1/accounts/${currentAccount.id}/agent_visibility_settings`,
      { hide_all_chats_for_agent: hideAllChatsForAgent.value },
      {
        headers: getAuthHeaders(),
        withCredentials: true,
      }
    );

    if (response.data.success) {
      useAlert(`Visibilidade de conversas ${hideAllChatsForAgent.value ? 'restrita' : 'liberada'} com sucesso!`);
    } else {
      throw new Error(response.data.error || 'Erro desconhecido');
    }
  } catch (error) {
    console.error('Erro ao atualizar configuração de visibilidade:', error);
    useAlert('Erro ao atualizar configuração de visibilidade. Tente novamente.');
    hideAllChatsForAgent.value = !hideAllChatsForAgent.value;
  }
};

const onToggleHideAllChatsForAgent = async () => {
  const newValue = !hideAllChatsForAgent.value;

  // Show confirmation modal
  const confirmed = await confirmationModal.value.showConfirmation();
  if (!confirmed) return;

  hideAllChatsForAgent.value = newValue;
  toggleHideAllChatsForAgent();
};

const fetchAutoAssignSetting = async () => {
  try {
    const currentAccount = store.getters.getCurrentAccount;
    const response = await axios.get(
      `/api/v1/accounts/${currentAccount.id}/auto_assign_settings`,
      {
        headers: getAuthHeaders(),
        withCredentials: true,
      }
    );

    if (response.data.success) {
      autoAssignEnabled.value = response.data.auto_assign_enabled;
    }
  } catch (error) {
    console.error('Erro ao buscar configuração de auto-atribuição:', error);
    autoAssignEnabled.value = false;
  }
};

const toggleAutoAssign = async () => {
  try {
    const currentAccount = store.getters.getCurrentAccount;
    const response = await axios.put(
      `/api/v1/accounts/${currentAccount.id}/auto_assign_settings`,
      { auto_assign_enabled: autoAssignEnabled.value },
      {
        headers: getAuthHeaders(),
        withCredentials: true,
      }
    );

    if (response.data.success) {
      useAlert(`Auto-atribuição ${autoAssignEnabled.value ? 'ativada' : 'desativada'} com sucesso!`);
    } else {
      throw new Error(response.data.error || 'Erro desconhecido');
    }
  } catch (error) {
    console.error('Erro ao atualizar configuração de auto-atribuição:', error);
    useAlert('Erro ao atualizar configuração de auto-atribuição. Tente novamente.');
    autoAssignEnabled.value = !autoAssignEnabled.value;
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('AGENT_MGMT.LOADING')"
    :no-records-found="!agentList.length"
    :no-records-message="$t('AGENT_MGMT.LIST.404')"
  >
    <template #header>
      <div class="flex flex-col items-start w-full pt-4">
        <div class="flex items-center justify-between w-full gap-4">
          <h1 class="text-2xl font-semibold font-interDisplay tracking-[0.3px] text-slate-900 dark:text-slate-25">
            <woot-sidemenu-icon />
            {{ $t('AGENT_MGMT.HEADER') }}
          </h1>
          <woot-button
            class="rounded-md button nice"
            icon="add-circle"
            @click="openAddPopup"
          >
            {{ $t('AGENT_MGMT.HEADER_BTN_TXT') }}
          </woot-button>
        </div>

        <div class="flex flex-col w-full gap-3 text-slate-600 dark:text-slate-300">
          <p class="mb-0 mt-2.5 text-base font-normal line-clamp-5 sm:line-clamp-none max-w-3xl tracking-[-0.1px]">
            {{ $t('AGENT_MGMT.DESCRIPTION') }}
          </p>
        </div>
      </div>
    </template>

    <template #body>
      <!-- Force Sign Message Toggle - Highlighted Section -->
      <div v-if="isAdmin" class="mb-6 p-3 sm:p-4 bg-n-slate-2 dark:bg-n-slate-1 border border-n-slate-3 dark:border-n-slate-6 rounded-lg">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
          <div class="flex items-center gap-3 sm:gap-4">
            <!-- Custom Large Toggle -->
            <button
                type="button"
                class="relative h-5 w-9 sm:h-6 sm:w-11 transition-colors duration-200 ease-in-out rounded-full focus:outline-none focus:ring-2 focus:ring-n-brand focus:ring-offset-2 focus:ring-offset-n-slate-2"
                :class="forceSignMessage ? 'bg-n-brand' : 'bg-n-slate-6 dark:bg-n-slate-7'"
                role="switch"
                :aria-checked="forceSignMessage"
                @click="forceSignMessage = !forceSignMessage; toggleForceSignMessage()"
              >
                <span class="sr-only">Alternar forçar assinatura de mensagem</span>
                <span
                  class="absolute top-1/2 left-0.5 h-4 w-4 sm:h-5 sm:w-5 transform -translate-y-1/2 rounded-full shadow-lg transition-transform duration-200 ease-in-out"
                  :class="forceSignMessage ? 'translate-x-4 sm:translate-x-5 bg-white' : 'translate-x-0 bg-white'"
                />
            </button>
            <div class="flex-1">
              <label class="text-sm sm:text-base font-semibold text-n-slate-12 block">
                Forçar todos os atendentes a assinar mensagens
              </label>
              <p class="text-sm text-n-slate-11 mt-1 hidden sm:block">
                Quando habilitado, todas as mensagens enviadas pelos atendentes incluirão automaticamente o nome do atendente responsável
              </p>
            </div>
          </div>
          <div class="flex items-center gap-2 justify-between sm:justify-end">
            <span class="text-sm font-medium text-n-slate-11">
              {{ forceSignMessage ? 'Ativado' : 'Desativado' }}
            </span>
            <div 
              class="w-2 h-2 rounded-full flex-shrink-0"
              :class="forceSignMessage ? 'bg-green-400' : 'bg-n-slate-8'"
            />
          </div>
        </div>
      </div>

      <!-- Auto Assign on Message Toggle -->
      <div v-if="isAdmin" class="mb-6 p-3 sm:p-4 bg-n-slate-2 dark:bg-n-slate-1 border border-n-slate-3 dark:border-n-slate-6 rounded-lg">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
          <div class="flex items-center gap-3 sm:gap-4">
            <!-- Custom Large Toggle -->
            <button
                type="button"
                class="relative h-5 w-9 sm:h-6 sm:w-11 transition-colors duration-200 ease-in-out rounded-full focus:outline-none focus:ring-2 focus:ring-n-brand focus:ring-offset-2 focus:ring-offset-n-slate-2"
                :class="autoAssignEnabled ? 'bg-n-brand' : 'bg-n-slate-6 dark:bg-n-slate-7'"
                role="switch"
                :aria-checked="autoAssignEnabled"
                @click="autoAssignEnabled = !autoAssignEnabled; toggleAutoAssign()"
              >
                <span class="sr-only">Alternar auto-atribuição ao enviar mensagem</span>
                <span
                  class="absolute top-1/2 left-0.5 h-4 w-4 sm:h-5 sm:w-5 transform -translate-y-1/2 rounded-full shadow-lg transition-transform duration-200 ease-in-out"
                  :class="autoAssignEnabled ? 'translate-x-4 sm:translate-x-5 bg-white' : 'translate-x-0 bg-white'"
                />
            </button>
            <div class="flex-1">
              <label class="text-sm sm:text-base font-semibold text-n-slate-12 block">
                Auto-atribuir conversas ao enviar mensagem
              </label>
              <p class="text-sm text-n-slate-11 mt-1 hidden sm:block">
                Quando habilitado, ao enviar uma mensagem em uma conversa não atribuída, ela será automaticamente atribuída ao atendente que enviou a mensagem
              </p>
            </div>
          </div>
          <div class="flex items-center gap-2 justify-between sm:justify-end">
            <span class="text-sm font-medium text-n-slate-11">
              {{ autoAssignEnabled ? 'Ativado' : 'Desativado' }}
            </span>
            <div
              class="w-2 h-2 rounded-full flex-shrink-0"
              :class="autoAssignEnabled ? 'bg-green-400' : 'bg-n-slate-8'"
            />
          </div>
        </div>
      </div>

      <!-- Hide All Chats For Agent Toggle -->
      <div v-if="isAdmin" class="mb-6 p-3 sm:p-4 bg-n-slate-2 dark:bg-n-slate-1 border border-n-slate-3 dark:border-n-slate-6 rounded-lg">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
          <div class="flex items-center gap-3 sm:gap-4">
            <!-- Custom Large Toggle -->
            <button
                type="button"
                class="relative h-5 w-9 sm:h-6 sm:w-11 transition-colors duration-200 ease-in-out rounded-full focus:outline-none focus:ring-2 focus:ring-n-brand focus:ring-offset-2 focus:ring-offset-n-slate-2"
                :class="hideAllChatsForAgent ? 'bg-n-brand' : 'bg-n-slate-6 dark:bg-n-slate-7'"
                role="switch"
                :aria-checked="hideAllChatsForAgent"
                @click="onToggleHideAllChatsForAgent"
              >
                <span class="sr-only">Alternar ocultar conversas de outros atendentes</span>
                <span
                  class="absolute top-1/2 left-0.5 h-4 w-4 sm:h-5 sm:w-5 transform -translate-y-1/2 rounded-full shadow-lg transition-transform duration-200 ease-in-out"
                  :class="hideAllChatsForAgent ? 'translate-x-4 sm:translate-x-5 bg-white' : 'translate-x-0 bg-white'"
                />
            </button>
            <div class="flex-1">
              <label class="text-sm sm:text-base font-semibold text-n-slate-12 block">
                Ocultar conversas de outros atendentes
              </label>
              <p class="text-sm text-n-slate-11 mt-1 hidden sm:block">
                Quando habilitado, os atendentes verão apenas as conversas atribuídas a eles. Administradores continuam vendo todas as conversas.
              </p>
            </div>
          </div>
          <div class="flex items-center gap-2 justify-between sm:justify-end">
            <span class="text-sm font-medium text-n-slate-11">
              {{ hideAllChatsForAgent ? 'Ativado' : 'Desativado' }}
            </span>
            <div
              class="w-2 h-2 rounded-full flex-shrink-0"
              :class="hideAllChatsForAgent ? 'bg-green-400' : 'bg-n-slate-8'"
            />
          </div>
        </div>
      </div>

      <table class="divide-y divide-slate-75 dark:divide-slate-700">
        <tbody class="divide-y divide-n-weak text-n-slate-11">
          <tr v-for="(agent, index) in agentList" :key="agent.email">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div class="flex flex-row items-center gap-4">
                <Thumbnail
                  :src="agent.thumbnail"
                  :username="agent.name"
                  size="40px"
                  :status="agent.availability_status"
                />
                <div>
                  <span class="block font-medium capitalize">
                    {{ agent.name }}
                  </span>
                  <span>{{ agent.email }}</span>
                </div>
              </div>
            </td>

            <td class="relative py-4 ltr:pr-4 rtl:pl-4">
              <span
                class="block font-medium w-fit"
                :class="{
                  'hover:text-gray-900 group cursor-pointer':
                    agent.custom_role_id,
                }"
              >
                {{ getAgentRoleName(agent) }}

                <div
                  class="absolute left-0 z-10 hidden max-w-[300px] w-auto bg-white rounded-xl border border-slate-50 shadow-lg top-14 md:top-12 dark:bg-slate-800 dark:border-slate-700"
                  :class="{ 'group-hover:block': agent.custom_role_id }"
                >
                  <div class="flex flex-col gap-1 p-4">
                    <span class="font-semibold">
                      {{ $t('AGENT_MGMT.LIST.AVAILABLE_CUSTOM_ROLE') }}
                    </span>
                    <ul class="pl-4 mb-0 list-disc">
                      <li
                        v-for="permission in getAgentRolePermissions(agent)"
                        :key="permission"
                        class="font-normal"
                      >
                        {{
                          $t(
                            `CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`
                          )
                        }}
                      </li>
                    </ul>
                  </div>
                </div>
              </span>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <span v-if="agent.confirmed">
                {{ $t('AGENT_MGMT.LIST.VERIFIED') }}
              </span>
              <span v-if="!agent.confirmed">
                {{ $t('AGENT_MGMT.LIST.VERIFICATION_PENDING') }}
              </span>
            </td>
            <td class="py-4">
              <div class="flex justify-end gap-1">
                <woot-button
                  v-if="showEditAction(agent)"
                  v-tooltip.top="$t('AGENT_MGMT.EDIT.BUTTON_TEXT')"
                  variant="smooth"
                  size="tiny"
                  color-scheme="secondary"
                  icon="edit"
                  class-names="grey-btn"
                  @click="openEditPopup(agent)"
                />
                <woot-button
                  v-if="showDeleteAction(agent)"
                  v-tooltip.top="$t('AGENT_MGMT.DELETE.BUTTON_TEXT')"
                  variant="smooth"
                  color-scheme="alert"
                  size="tiny"
                  icon="dismiss-circle"
                  class-names="grey-btn"
                  :is-loading="loading[agent.id]"
                  @click="openDeletePopup(agent, index)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddAgent @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditAgent
        v-if="showEditPopup"
        :id="currentAgent.id"
        :name="currentAgent.name"
        :type="currentAgent.role"
        :email="currentAgent.email"
        :availability="currentAgent.availability_status"
        :custom-role-id="currentAgent.custom_role_id"
        @close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeletePopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('AGENT_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('AGENT_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />

    <ConfirmationModal
      ref="confirmationModal"
      title="Confirmar alteração"
      description="Ao ativar esta opção, os atendentes passarão a ver apenas conversas atribuídas a eles. Administradores continuarão vendo todas as conversas. Deseja continuar?"
      confirm-label="Confirmar"
      cancel-label="Cancelar"
    />
  </SettingsLayout>
</template>
